import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { ApiError } from "@finora/api-client";
import { Button, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";
import { ModuleGateNotice } from "../../components/ModuleGateNotice";

const input = "w-full rounded-lg border border-border bg-white px-3 py-1.5 text-sm";

export function WooConnectorTab() {
  const queryClient = useQueryClient();
  const { data, isLoading, error: loadError } = useQuery({
    queryKey: ["woo-connector"],
    queryFn: () => api.getWooConnector(),
    retry: false,
  });
  const [siteUrl, setSiteUrl] = useState("");
  const [mode, setMode] = useState<"PLUGIN" | "WC_KEYS">("PLUGIN");
  const [token, setToken] = useState("");
  const [ck, setCk] = useState("");
  const [cs, setCs] = useState("");
  const [active, setActive] = useState(true);
  const [pricesNet, setPricesNet] = useState(true);
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setSiteUrl(data.siteUrl);
      setMode(data.mode);
      setActive(data.isActive);
      setPricesNet(data.pricesNet ?? true);
    }
  }, [data]);

  const errText = (e: unknown) => (e instanceof ApiError ? e.response.message : "Greška");
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["woo-connector"] });

  const save = useMutation({
    mutationFn: () =>
      api.saveWooConnector({
        siteUrl,
        mode,
        token: mode === "PLUGIN" && token ? token : undefined,
        consumerKey: mode === "WC_KEYS" && ck ? ck : undefined,
        consumerSecret: mode === "WC_KEYS" && cs ? cs : undefined,
        isActive: active,
        pricesNet,
      }),
    onSuccess: () => {
      setToken("");
      setCk("");
      setCs("");
      setMsg("Sačuvano. Kliknite „Testiraj vezu“.");
      void refresh();
    },
    onError: (e) => setMsg(errText(e)),
  });
  const test = useMutation({
    mutationFn: () => api.testWooConnector(code || undefined),
    onSuccess: (r) => {
      if (!r.ok) return setMsg(r.error ?? "Veza nije uspela.");
      const sample = r.sampleSearched ? (r.sample ? ` Artikal: ${r.sample.name} – ${r.sample.price} RSD.` : " Artikal sa tom šifrom nije pronađen na sajtu.") : "";
      setMsg(`Veza radi (${r.info}).${sample}`);
      void refresh();
    },
    onError: (e) => setMsg(errText(e)),
  });
  const remove = useMutation({
    mutationFn: () => api.deleteWooConnector(),
    onSuccess: () => {
      setSiteUrl("");
      setMsg("Konektor uklonjen.");
      void refresh();
    },
  });

  if (loadError instanceof ApiError && loadError.response.errorCode === "MODULE_NOT_AVAILABLE") {
    return (
      <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <h2 className="mb-2 text-sm font-semibold text-slate-800">WooCommerce konektor</h2>
        <ModuleGateNotice error={loadError} moduleKey="woocommerce_connector" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-4">
      <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <h2 className="mb-1 text-sm font-semibold text-slate-800">WooCommerce konektor</h2>
        <p className="text-xs text-slate-500">
          Kad na kasi ukucate artikal, HAFiskal pita vašu WooCommerce prodavnicu i sam povuče <strong>naziv i cenu</strong> sa sajta, pa ih ne morate
          ručno da kopirate. Artikli se povezuju preko šifre (SKU) – ista šifra mora postojati u HAFiskalu i na sajtu. Ako sajt ne odgovara, kasa
          nastavlja sa podacima iz HAFiskal-a.
        </p>
      </div>

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <div className="space-y-3 rounded-xl border border-border bg-surface p-4 shadow-subtle">
          {!data && (
            <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
              <p className="mb-1 font-semibold">Kako da povežete sajt (preporučeno, bez ikakvog tehničkog znanja)</p>
              <ol className="list-decimal space-y-0.5 pl-4">
                <li>
                  Preuzmite dodatak:{" "}
                  <a className="inline-flex items-center gap-1 underline" href="/downloads/hafiskal-woo-connector.zip" download>
                    <Download className="h-3 w-3" aria-hidden /> hafiskal-woo-connector.zip
                  </a>
                </li>
                <li>U WordPress-u: Dodaci → Dodaj novi → Otpremi dodatak → izaberite preuzeti fajl → Instaliraj → Aktiviraj.</li>
                <li>Otvorite WooCommerce → HAFiskal konektor. Tamo su adresa sajta i ključ – prepišite ih ovde ispod.</li>
              </ol>
            </div>
          )}

          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-1.5">
              <input type="radio" checked={mode === "PLUGIN"} onChange={() => setMode("PLUGIN")} /> Pomoću HAFiskal plugina (preporučeno)
            </label>
            <label className="flex items-center gap-1.5">
              <input type="radio" checked={mode === "WC_KEYS"} onChange={() => setMode("WC_KEYS")} /> Pomoću WooCommerce ključeva
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="col-span-2 text-xs text-slate-500">
              Adresa sajta
              <input className={input} placeholder="https://mojaprodavnica.rs" value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} />
            </label>
            {mode === "PLUGIN" ? (
              <label className="col-span-2 text-xs text-slate-500">
                Ključ iz plugina {data ? "(ostavite prazno da ostane isti)" : ""}
                <input className={input} type="password" autoComplete="new-password" value={token} onChange={(e) => setToken(e.target.value)} />
              </label>
            ) : (
              <>
                <div className="col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                  <p className="mb-1 font-medium text-slate-700">Gde se nalaze ključevi?</p>
                  <ol className="list-decimal space-y-0.5 pl-4">
                    <li>U WordPress-u otvorite WooCommerce → Podešavanja → Napredno → REST API.</li>
                    <li>Kliknite „Dodaj ključ“, upišite bilo koji naziv (npr. HAFiskal), u polju „Dozvole“ izaberite „Čitanje“.</li>
                    <li>Kliknite „Generiši API ključ“ i prepišite oba ključa u polja ispod.</li>
                  </ol>
                  <p className="mt-1 text-slate-500">Napomena: WooCommerce prikazuje ključeve samo jednom, odmah posle pravljenja.</p>
                </div>
                <label className="text-xs text-slate-500">
                  Korisnički ključ (počinje sa ck_)
                  <input className={input} placeholder="ck_..." type="password" autoComplete="new-password" value={ck} onChange={(e) => setCk(e.target.value)} />
                </label>
                <label className="text-xs text-slate-500">
                  Tajni ključ (počinje sa cs_) {data ? "(prazno = ostaje isti)" : ""}
                  <input className={input} placeholder="cs_..." type="password" autoComplete="new-password" value={cs} onChange={(e) => setCs(e.target.value)} />
                </label>
              </>
            )}
            <label className="flex items-center gap-2 text-xs text-slate-500">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Uključeno
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-500">
              <input type="checkbox" checked={pricesNet} onChange={(e) => setPricesNet(e.target.checked)} /> Cene na sajtu su bez PDV-a (HAFiskal dodaje PDV)
            </label>
          </div>

          <div className="flex flex-wrap items-end gap-2">
            <Button size="sm" disabled={save.isPending || !siteUrl} onClick={() => save.mutate()}>
              Sačuvaj
            </Button>
            {data && (
              <>
                <input className="h-8 w-44 rounded-lg border border-border px-2 text-xs" placeholder="Šifra artikla za probu (SKU)" value={code} onChange={(e) => setCode(e.target.value)} />
                <Button size="sm" disabled={test.isPending} onClick={() => test.mutate()}>
                  Testiraj vezu
                </Button>
                <button className="text-xs text-slate-400 hover:text-red-600" onClick={() => window.confirm("Ukloniti WooCommerce konektor?") && remove.mutate()}>
                  Ukloni
                </button>
              </>
            )}
          </div>
          {msg && <p className="text-xs text-slate-600">{msg}</p>}
          {data && (
            <p className="text-xs text-slate-400">
              Poslednja provera: {data.lastCheckAt ? new Date(data.lastCheckAt).toLocaleString("sr-RS") : "nikad"}
              {data.lastStatus ? ` · ${data.lastStatus === "OK" ? "u redu" : "greška"}` : ""}
              {data.lastError ? ` · ${data.lastError}` : ""}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
